import React, { useEffect, useRef } from 'react';

import { AnimatePresence, motion, type Variant, type Variants } from 'framer-motion';
import { useMeasure } from 'react-use';

import { useOnClickOutside } from '@app/helpers';

import { type DropdownPanelProps } from './types';

import {
    Chevron,
    CHEVRON_WIDTH,
    ChevronWrapper,
    DROPDOWN_WIDTH_OPEN,
    DropdownPanelContainer,
    DropdownPanelContent,
    DropdownPanelTrigger,
    DropdownPanelWrapper,
} from './styles';

export enum VariantKey {
    open = 'open',
    closed = 'closed',
}

const ANIMATION_OPEN_DURATION = 0.3;
const ANIMATION_CLOSE_DURATION = 0.2;
const VARIANTS_DROPDOWN_BASE = {
    [VariantKey.open]: {
        height: 'auto',
        width: DROPDOWN_WIDTH_OPEN,
        padding: '10px 0 0 10px',
        top: '-10px',
        left: '-10px',
        background: '#000000',
        transition: {
            duration: ANIMATION_OPEN_DURATION,
            bounce: 0,
            background: {
                duration: 0.1,
                easing: 'linear',
            },
        },
    },
    [VariantKey.closed]: {
        width: 'auto',
        top: '0',
        left: '0',
        padding: '0',
        background: '#222030',
        transition: {
            bounce: 0,
            duration: ANIMATION_CLOSE_DURATION,
        },
    },
};

const VARIANTS_CHEVRON: Variants = {
    closed: { scaleY: 1 },
    open: { scaleY: -1, transition: { duration: 0.2 } },
};

// The width and height of thew trigger element can change, and therefore we need to update the closed variant
const getVariantsDropdownPanel = (
    widthOfDropdownPanelTrigger?: number,
    heightOfDropdownPanelTrigger?: number,
): Record<VariantKey, Variant> => {
    const closedWidth = widthOfDropdownPanelTrigger ? widthOfDropdownPanelTrigger + CHEVRON_WIDTH : 'auto';
    const closedHeight = heightOfDropdownPanelTrigger ? heightOfDropdownPanelTrigger : 'auto';
    return {
        [VariantKey.open]: {
            ...VARIANTS_DROPDOWN_BASE[VariantKey.open],
            width: DROPDOWN_WIDTH_OPEN,
        },
        [VariantKey.closed]: {
            ...VARIANTS_DROPDOWN_BASE[VariantKey.closed],
            width: closedWidth,
            height: closedHeight,
        },
    };
};

export const DropdownPanel = ({ children, trigger, open, onClick, onClickOutside, isRelative }: DropdownPanelProps) => {
    const [dropdownMeasureRef, { width: widthOfDropdownPanelTrigger, height: heightOfDropdownPanelTrigger }] =
        useMeasure<HTMLDivElement>();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const variantsDropdownPanel = useRef<Variants>(
        getVariantsDropdownPanel(widthOfDropdownPanelTrigger, heightOfDropdownPanelTrigger),
    );
    useOnClickOutside(dropdownRef, () => {
        if (onClickOutside) {
            onClickOutside();
        }
    });
    useEffect(() => {
        if (widthOfDropdownPanelTrigger && heightOfDropdownPanelTrigger) {
            variantsDropdownPanel.current = getVariantsDropdownPanel(
                widthOfDropdownPanelTrigger,
                heightOfDropdownPanelTrigger,
            );
        }
    }, [widthOfDropdownPanelTrigger, heightOfDropdownPanelTrigger]);
    return (
        <DropdownPanelWrapper ref={dropdownRef} style={{ height: heightOfDropdownPanelTrigger }}>
            <DropdownPanelContainer
                animate={open ? 'open' : 'closed'}
                variants={variantsDropdownPanel.current}
                initial="closed"
                key={widthOfDropdownPanelTrigger}
                $isRelative={isRelative}
            >
                <DropdownPanelTrigger onClick={onClick}>
                    <div ref={dropdownMeasureRef}>{trigger}</div>
                    <ChevronWrapper variants={VARIANTS_CHEVRON}>
                        <Chevron />
                    </ChevronWrapper>
                </DropdownPanelTrigger>
                <AnimatePresence>
                    {open && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                            <DropdownPanelContent>{children}</DropdownPanelContent>
                        </motion.div>
                    )}
                </AnimatePresence>
            </DropdownPanelContainer>
        </DropdownPanelWrapper>
    );
};
